'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';

const WF = {
  dark: '#16232F',
  amber: '#D97A2E',
  cream: '#F6F3EC',
  darkMid: '#1E2F3E',
  amberDim: 'rgba(217,122,46,0.15)',
  textLight: 'rgba(246,243,236,0.85)',
  textMuted: 'rgba(246,243,236,0.55)',
  darkBorder: 'rgba(246,243,236,0.12)',
  creamDark: '#EDE9DF',
  inkDark: '#16232F',
  inkMid: '#2C3E50',
  inkLight: '#4A5568',
};

const SG: React.CSSProperties = { fontFamily: "'Space Grotesk', sans-serif" };
const INTER: React.CSSProperties = { fontFamily: "'Inter', sans-serif" };
const MONO: React.CSSProperties = { fontFamily: "'IBM Plex Mono', monospace" };

const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('wf-visible');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = document.querySelectorAll('.wf-reveal');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const processSteps = [
  {
    num: '01',
    title: 'Employer Requirement',
    desc: 'Receive manpower quantity, job descriptions, qualifications, experience, salary/conditions, location and target joining date.',
  },
  {
    num: '02',
    title: 'Requirement Assessment',
    desc: 'Review the contract and determine the candidate profile, sourcing requirements and anticipated recruitment timeline.',
  },
  {
    num: '03',
    title: 'Candidate Sourcing',
    desc: 'Source suitable candidates through our recruitment network and appropriate recruitment channels.',
  },
  {
    num: '04',
    title: 'Screening & Shortlisting',
    desc: 'Review qualifications, experience, skills, suitability, availability and relevant documentation.',
  },
  {
    num: '05',
    title: 'Employer Interview & Selection',
    desc: 'Present shortlisted candidates to the employer for interview, assessment and final selection.',
  },
  {
    num: '06',
    title: 'Employment & Documentation',
    desc: 'Coordinate the required employment documents and information with the employer and selected candidates.',
  },
  {
    num: '07',
    title: 'Work Permit / Visa Process',
    desc: "The employer and authorised parties proceed with the applicable work-permit and immigration procedures under the destination country\'s rules.",
  },
  {
    num: '08',
    title: 'Mobilisation & Joining',
    desc: 'Following the required approvals and formalities, candidates are prepared for travel/mobilisation and commencement of employment.',
  },
];

const industries = [
  'Construction & Infrastructure',
  'Hospitality & Tourism',
  'Logistics & Warehousing',
  'Manufacturing & Production',
  'Facility Management',
  'Maintenance & Technical Services',
  'Agriculture & Seasonal Workforce',
  'Administration & Business Support',
  'Other contract-specific requirements',
];

const checklist = [
  'Company details',
  'Manpower quantity',
  'Job titles and descriptions',
  'Required skills and qualifications',
  'Experience requirements',
  'Employment location',
  'Salary and benefits',
  'Working hours/conditions',
  'Contract duration',
  'Target joining date',
  'Accommodation/transport/other benefits where applicable',
  'Any specific work-permit or immigration requirements known to the employer',
];

export default function WorkforceSolutionsPage() {
  useScrollReveal();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .wf-reveal { opacity: 0; transform: translateY(22px); transition: opacity 0.6s cubic-bezier(0.25,0.1,0.25,1), transform 0.6s cubic-bezier(0.25,0.1,0.25,1); }
        .wf-visible { opacity: 1 !important; transform: translateY(0) !important; }
        .wf-card { transition: transform 0.25s cubic-bezier(0.25,0.1,0.25,1), box-shadow 0.25s cubic-bezier(0.25,0.1,0.25,1); }
        .wf-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(22,35,47,0.14); }
        .wf-step:hover { border-color: rgba(217,122,46,0.45) !important; background-color: rgba(217,122,46,0.03) !important; }
        .wf-btn-amber { transition: background-color 0.2s, transform 0.2s, box-shadow 0.2s; }
        .wf-btn-amber:hover { background-color: #C06A20 !important; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(217,122,46,0.35); }
        .wf-btn-outline { transition: background-color 0.2s, transform 0.2s; }
        .wf-btn-outline:hover { background-color: rgba(246,243,236,0.1) !important; transform: translateY(-2px); }
        @media (min-width: 768px) {
          .wf-bento-1 { grid-column: span 7 !important; }
          .wf-bento-2 { grid-column: span 5 !important; }
          .wf-bento-3 { grid-column: span 5 !important; }
          .wf-bento-4 { grid-column: span 7 !important; }
        }
      `}</style>

      <main id="main-content" style={{ backgroundColor: WF.cream, ...INTER }}>

        {/* ── 1. HERO ─────────────────────────────────────────── */}
        <section style={{ backgroundColor: WF.dark, paddingTop: '120px', paddingBottom: '96px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, transparent, ${WF.amber}, transparent)` }} />
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(217,122,46,0.055) 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />
          <div className="max-w-6xl mx-auto px-6" style={{ position: 'relative', zIndex: 1 }}>
            <div className="wf-reveal">
              <p style={{ ...MONO, fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: WF.amber, marginBottom: '20px' }}>
                International Workforce &amp; Contract Manpower
              </p>
              <h1 style={{ ...SG, fontSize: 'clamp(2.4rem, 5.5vw, 4rem)', fontWeight: 700, color: WF.cream, lineHeight: 1.1, letterSpacing: '-0.025em', marginBottom: '24px', maxWidth: '780px' }}>
                Skilled people,{' '}
                <span style={{ color: WF.amber }}>mobilised on schedule.</span>
              </h1>
              <p style={{ ...INTER, fontSize: '1.05rem', lineHeight: 1.8, color: WF.textLight, maxWidth: '600px', marginBottom: '40px' }}>
                Nexus Vantage sources, screens and mobilises contract manpower for European employers — matched to the job, ready for the deployment date.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <Link
                  href="/contact"
                  className="wf-btn-amber"
                  style={{ ...INTER, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '14px 28px', backgroundColor: WF.amber, color: '#fff', fontWeight: 600, fontSize: '0.95rem', borderRadius: '8px', textDecoration: 'none' }}
                >
                  Share Your Requirement
                </Link>
                <a
                  href="#process"
                  className="wf-btn-outline"
                  style={{ ...INTER, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '14px 28px', border: '2px solid rgba(246,243,236,0.3)', color: WF.cream, fontWeight: 600, fontSize: '0.95rem', borderRadius: '8px', textDecoration: 'none' }}
                >
                  See How It Works
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── 2. WHO WE ARE ───────────────────────────────────── */}
        <section style={{ backgroundColor: WF.cream, padding: '96px 0' }}>
          <div className="max-w-6xl mx-auto px-6">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '48px', alignItems: 'start' }}>
              <div className="wf-reveal">
                <p style={{ ...MONO, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: WF.amber, marginBottom: '16px' }}>
                  Who We Are
                </p>
                <h2 style={{ ...SG, fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 700, color: WF.inkDark, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                  A Portugal-based<br />international consultancy
                </h2>
              </div>
              <div className="wf-reveal" style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                <p style={{ ...INTER, fontSize: '1rem', lineHeight: 1.8, color: WF.inkMid }}>
                  Nexus Vantage Ltd. is a Portugal-based international consultancy developing workforce solutions for European companies requiring skilled, reliable and suitable people for contract, project-based and longer-term employment requirements. Our approach is based on understanding the employer&apos;s actual requirement before sourcing candidates.
                </p>
                <p style={{ ...INTER, fontSize: '1rem', lineHeight: 1.8, color: WF.inkMid }}>
                  Our management team has practical experience in contractual manpower recruitment and workforce deployment in Gulf countries, where similar employer-driven manpower requirements were handled. This background provides practical understanding of candidate sourcing, employer expectations, contractual manpower requirements, coordination and workforce mobilisation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. OUR WORKFORCE SOLUTION (bento grid) ──────────── */}
        <section style={{ backgroundColor: WF.creamDark, padding: '96px 0' }}>
          <div className="max-w-6xl mx-auto px-6">
            <div className="wf-reveal" style={{ marginBottom: '48px' }}>
              <p style={{ ...MONO, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: WF.amber, marginBottom: '12px' }}>
                Our Approach
              </p>
              <h2 style={{ ...SG, fontSize: 'clamp(1.8rem, 3.5vw, 2.4rem)', fontWeight: 700, color: WF.inkDark, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                Our Workforce Solution
              </h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '18px' }}>
              {/* Card 1 — dark, wide */}
              <div
                className="wf-bento-1 wf-card wf-reveal"
                style={{ gridColumn: 'span 12', padding: '36px', backgroundColor: WF.dark, borderRadius: '12px', border: `1px solid ${WF.darkBorder}` }}
              >
                <div style={{ ...MONO, fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: WF.amber, marginBottom: '16px' }}>01</div>
                <h3 style={{ ...SG, fontSize: '1.3rem', fontWeight: 700, color: WF.cream, marginBottom: '10px', letterSpacing: '-0.01em' }}>Skilled Manpower</h3>
                <p style={{ ...INTER, fontSize: '0.93rem', lineHeight: 1.75, color: WF.textLight, margin: 0 }}>Candidates selected according to the technical and professional requirements of the position.</p>
              </div>
              {/* Card 2 — light, narrow */}
              <div
                className="wf-bento-2 wf-card wf-reveal"
                style={{ gridColumn: 'span 12', padding: '36px', backgroundColor: '#fff', borderRadius: '12px', border: `1px solid ${WF.creamDark}`, borderTop: `3px solid ${WF.amber}` }}
              >
                <div style={{ ...MONO, fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: WF.amber, marginBottom: '16px' }}>02</div>
                <h3 style={{ ...SG, fontSize: '1.3rem', fontWeight: 700, color: WF.inkDark, marginBottom: '10px', letterSpacing: '-0.01em' }}>Understanding People</h3>
                <p style={{ ...INTER, fontSize: '0.93rem', lineHeight: 1.75, color: WF.inkLight, margin: 0 }}>We seek candidates who understand the job, responsibilities, workplace expectations and contractual conditions.</p>
              </div>
              {/* Card 3 — light, narrow */}
              <div
                className="wf-bento-3 wf-card wf-reveal"
                style={{ gridColumn: 'span 12', padding: '36px', backgroundColor: '#fff', borderRadius: '12px', border: `1px solid ${WF.creamDark}`, borderTop: `3px solid ${WF.amber}` }}
              >
                <div style={{ ...MONO, fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: WF.amber, marginBottom: '16px' }}>03</div>
                <h3 style={{ ...SG, fontSize: '1.3rem', fontWeight: 700, color: WF.inkDark, marginBottom: '10px', letterSpacing: '-0.01em' }}>Requirement-Based Sourcing</h3>
                <p style={{ ...INTER, fontSize: '0.93rem', lineHeight: 1.75, color: WF.inkLight, margin: 0 }}>Candidate selection starts from the employer&apos;s job description, quantity, skills, experience and deployment schedule.</p>
              </div>
              {/* Card 4 — amber tint, wide */}
              <div
                className="wf-bento-4 wf-card wf-reveal"
                style={{ gridColumn: 'span 12', padding: '36px', backgroundColor: WF.amberDim, borderRadius: '12px', border: '1px solid rgba(217,122,46,0.25)' }}
              >
                <div style={{ ...MONO, fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: WF.amber, marginBottom: '16px' }}>04</div>
                <h3 style={{ ...SG, fontSize: '1.3rem', fontWeight: 700, color: WF.inkDark, marginBottom: '10px', letterSpacing: '-0.01em' }}>International Recruitment</h3>
                <p style={{ ...INTER, fontSize: '0.93rem', lineHeight: 1.75, color: WF.inkMid, margin: 0 }}>We work with appropriate sourcing networks and recruitment partners to identify suitable international candidates.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. ADVANCE PLANNING CALLOUT ─────────────────────── */}
        <section style={{ backgroundColor: WF.dark, padding: '96px 0', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', right: '-100px', top: '-100px', width: '450px', height: '450px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,122,46,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div className="max-w-6xl mx-auto px-6" style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '56px', alignItems: 'start' }}>
              <div className="wf-reveal">
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', backgroundColor: 'rgba(217,122,46,0.12)', border: '1px solid rgba(217,122,46,0.3)', borderRadius: '6px', padding: '8px 14px', marginBottom: '28px' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={WF.amber} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span style={{ ...MONO, fontSize: '0.67rem', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: WF.amber }}>Important Note</span>
                </div>
                <h2 style={{ ...SG, fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 700, color: WF.cream, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                  Manpower isn&apos;t available the moment a contract is awarded.
                </h2>
              </div>
              <div className="wf-reveal" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <p style={{ ...INTER, fontSize: '0.96rem', lineHeight: 1.8, color: WF.textLight }}>
                  International manpower cannot always be supplied immediately after a contract is awarded. After the employer confirms its requirement, time may be needed for candidate sourcing, screening, interviews, selection, documentation, employment formalities and applicable work-permit/visa procedures. Processing times can vary by country, occupation, employer and competent authorities.
                </p>
                <div style={{ borderLeft: `3px solid ${WF.amber}`, paddingLeft: '20px' }}>
                  <p style={{ ...INTER, fontSize: '0.96rem', lineHeight: 1.8, color: WF.textLight, margin: 0 }}>
                    Employers are encouraged to share their anticipated manpower requirements with Nexus Vantage well in advance of the intended deployment date. Early confirmation enables us to plan candidate sourcing, screening, interviews, selection, documentation and applicable work-permit/visa procedures in a structured manner. Advance planning can help reduce recruitment delays and improve workforce readiness for the planned commencement of the contract.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. KEY INDUSTRIES ───────────────────────────────── */}
        <section style={{ backgroundColor: WF.cream, padding: '96px 0' }}>
          <div className="max-w-6xl mx-auto px-6">
            <div className="wf-reveal" style={{ marginBottom: '40px' }}>
              <p style={{ ...MONO, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: WF.amber, marginBottom: '12px' }}>
                Sectors
              </p>
              <h2 style={{ ...SG, fontSize: 'clamp(1.8rem, 3.5vw, 2.4rem)', fontWeight: 700, color: WF.inkDark, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                Key Industries
              </h2>
            </div>
            <div className="wf-reveal" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {industries.map((industry, i) => (
                <span
                  key={i}
                  style={{
                    ...INTER,
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '10px 18px',
                    backgroundColor: i === industries.length - 1 ? 'transparent' : '#fff',
                    border: `1.5px solid ${i === industries.length - 1 ? 'rgba(22,35,47,0.22)' : WF.creamDark}`,
                    borderRadius: '100px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: WF.inkMid,
                    fontStyle: i === industries.length - 1 ? 'italic' : 'normal',
                  }}
                >
                  {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── 6. PROCESS ──────────────────────────────────────── */}
        <section id="process" style={{ backgroundColor: WF.creamDark, padding: '96px 0' }}>
          <div className="max-w-6xl mx-auto px-6">
            <div className="wf-reveal" style={{ marginBottom: '52px' }}>
              <p style={{ ...MONO, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: WF.amber, marginBottom: '12px' }}>
                How It Works
              </p>
              <h2 style={{ ...SG, fontSize: 'clamp(1.8rem, 3.5vw, 2.4rem)', fontWeight: 700, color: WF.inkDark, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                Our Recruitment Process
              </h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px' }}>
              {processSteps.map((step, i) => (
                <div
                  key={i}
                  className="wf-step wf-reveal"
                  style={{
                    display: 'flex',
                    gap: '18px',
                    padding: '26px 22px',
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    border: `1px solid ${WF.creamDark}`,
                    transition: 'border-color 0.2s, background-color 0.2s',
                    transitionDelay: `${(i % 4) * 0.07}s`,
                  }}
                >
                  <div style={{ flexShrink: 0, paddingTop: '2px' }}>
                    <span style={{ ...MONO, fontSize: '0.78rem', fontWeight: 500, color: WF.amber, letterSpacing: '0.04em' }}>{step.num}</span>
                  </div>
                  <div>
                    <h3 style={{ ...SG, fontSize: '0.97rem', fontWeight: 700, color: WF.inkDark, marginBottom: '7px', letterSpacing: '-0.01em' }}>{step.title}</h3>
                    <p style={{ ...INTER, fontSize: '0.86rem', lineHeight: 1.75, color: WF.inkLight, margin: 0 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 7. WHAT WE REQUEST FROM EMPLOYERS ───────────────── */}
        <section style={{ backgroundColor: WF.cream, padding: '96px 0' }}>
          <div className="max-w-6xl mx-auto px-6">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '56px', alignItems: 'start' }}>
              <div className="wf-reveal">
                <p style={{ ...MONO, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: WF.amber, marginBottom: '12px' }}>
                  Employer Information
                </p>
                <h2 style={{ ...SG, fontSize: 'clamp(1.8rem, 3.5vw, 2.4rem)', fontWeight: 700, color: WF.inkDark, lineHeight: 1.15, letterSpacing: '-0.02em', marginBottom: '20px' }}>
                  What We Request From Employers
                </h2>
                <p style={{ ...INTER, fontSize: '0.95rem', lineHeight: 1.75, color: WF.inkLight }}>
                  To begin the recruitment process, we ask employers to provide the following information so we can assess the requirement and plan accordingly.
                </p>
              </div>
              <div className="wf-reveal">
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '13px' }}>
                  {checklist.map((item, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <span style={{ flexShrink: 0, width: '20px', height: '20px', borderRadius: '50%', backgroundColor: WF.amberDim, border: '1.5px solid rgba(217,122,46,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px' }}>
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                          <path d="M2 6l3 3 5-5" stroke={WF.amber} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span style={{ ...INTER, fontSize: '0.9rem', lineHeight: 1.65, color: WF.inkMid }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── 8. CONTACT ──────────────────────────────────────── */}
        <section style={{ backgroundColor: WF.dark, padding: '96px 0', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, transparent, ${WF.amber}, transparent)` }} />
          <div className="max-w-6xl mx-auto px-6">
            <div style={{ maxWidth: '640px' }} className="wf-reveal">
              <p style={{ ...MONO, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: WF.amber, marginBottom: '16px' }}>
                Get In Touch
              </p>
              <h2 style={{ ...SG, fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)', fontWeight: 700, color: WF.cream, lineHeight: 1.15, letterSpacing: '-0.02em', marginBottom: '36px' }}>
                Share your workforce requirement with us.
              </h2>
              <div style={{ backgroundColor: 'rgba(246,243,236,0.06)', border: `1px solid ${WF.darkBorder}`, borderRadius: '12px', padding: '32px', marginBottom: '28px' }}>
                <p style={{ ...SG, fontSize: '1.05rem', fontWeight: 600, color: WF.cream, marginBottom: '4px' }}>Amrutha Nambiar</p>
                <p style={{ ...INTER, fontSize: '0.875rem', color: WF.textMuted, marginBottom: '2px' }}>Managing Director</p>
                <p style={{ ...INTER, fontSize: '0.875rem', color: WF.textMuted, marginBottom: '24px' }}>Nexus Vantage Ltd., Portugal</p>
                <a
                  href="/contact"
                  className="wf-btn-amber"
                  style={{ ...INTER, display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '13px 24px', backgroundColor: WF.amber, color: '#fff', fontWeight: 600, fontSize: '0.9rem', borderRadius: '8px', textDecoration: 'none' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  contact@nexusvantage.eu
                </a>
              </div>
              <p style={{ ...INTER, fontSize: '0.775rem', lineHeight: 1.7, color: WF.textMuted }}>
                Recruitment, employment, work-permit and immigration procedures are subject to the laws, eligibility requirements, processing timelines and decisions of the relevant destination-country authorities. Nexus Vantage does not guarantee approval or a fixed processing time.
              </p>
            </div>
          </div>
        </section>

      </main>
    </>
  );
}
